import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {configKeys} from '../patient.config-keys';


/**
 * Service to fetch the thieme monthly submission cap state from the thieme connector.
 * No client side caching is done here, as the connector already caches the cap state
 * server side (60s ttl, invalidated on submissions).
 */
@Injectable()
export class ThiemeCapService {
  public constructor(
    private registryService: RegistryService,
    private configService: ConfigService,
    private http: HttpClient
  ) {
  }

  /**
   * Loads the current thieme cap state
   *
   * @returns {Promise<{used: number, limit: number, remaining: number}>}
   */
  public getCap(): Promise<{used: number, limit: number, remaining: number}> {
    const basePath = `${this.configService.get(configKeys.THIEME_CONNECTOR_URI_CONFIG_KEY)}/cap`;

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          (cap: any) => {
            // auth refusals arrive as http 200 with an error body, so validate the payload shape
            if (!cap || typeof cap.remaining !== 'number') {
              reject(new Error((cap && cap.message) || 'invalid thieme cap response'));
              return;
            }

            resolve({used: cap.used || 0, limit: cap.limit || 0, remaining: cap.remaining || 0});
          },
          error => reject(error)
        );
    });
  }

  /**
   * Returns auth headers by auth token and tenant id
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get(configKeys.SESSION_AUTH_TOKEN);
    const tenantId = this.registryService.get(configKeys.SESSION_TENANT_ID);

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
